import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getTrafficStats, type TrafficBucket } from "@/lib/traffic.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BLOCKED_COUNTRIES } from "@/lib/bot-detection";

export const Route = createFileRoute("/_admin/admin/traffic")({
  head: () => ({
    meta: [
      { title: "Traffic-Analyse – creategy Backend" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: TrafficPage,
});

const RANGES = [
  { label: "24 Stunden", hours: 24 },
  { label: "7 Tage", hours: 24 * 7 },
  { label: "30 Tage", hours: 24 * 30 },
];

function BucketTable({ title, buckets, note }: { title: string; buckets: TrafficBucket[]; note?: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {note ? <p className="text-sm text-muted-foreground">{note}</p> : null}
      </CardHeader>
      <CardContent>
        {buckets.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine Daten im gewählten Zeitraum.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {buckets.map((b) => (
              <li key={b.key} className="flex items-center justify-between gap-4 py-2">
                <span className="min-w-0 flex-1 break-all">{b.key}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {b.total}
                  {b.suspicious > 0 ? ` · ${b.suspicious} Bot` : ""}
                  {b.blocked > 0 ? ` · ${b.blocked} gesperrt` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function TrafficPage() {
  const [hours, setHours] = useState(24);
  const fetchStats = useServerFn(getTrafficStats);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "traffic", hours],
    queryFn: () => fetchStats({ data: { hours } }),
    refetchOnWindowFocus: false,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Traffic &amp; Bot-Analyse</h1>
          <p className="text-sm text-muted-foreground">
            Serverseitiges Protokoll der Seitenaufrufe (30 Tage Aufbewahrung, IP nur pseudonymisiert).
            Gesperrte Länder: {[...BLOCKED_COUNTRIES].join(", ")}.
          </p>
        </div>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <Button
              key={r.hours}
              variant={hours === r.hours ? "default" : "outline"}
              size="sm"
              onClick={() => setHours(r.hours)}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </div>

      {error ? (
        <p className="text-sm text-destructive">Auswertung konnte nicht geladen werden.</p>
      ) : isLoading || !data ? (
        <p className="text-sm text-muted-foreground">Wird geladen …</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {[
              { label: "Aufrufe", value: data.total },
              { label: "Menschlich", value: data.humans },
              { label: "Erlaubte Bots", value: data.goodBots },
              { label: "Verdächtige Bots", value: data.suspicious },
              { label: "Gesperrt", value: data.blocked },
            ].map((kpi) => (
              <Card key={kpi.label}>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">{kpi.label}</p>
                  <p className="text-2xl font-semibold tabular-nums">{kpi.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <BucketTable title="Länder" buckets={data.countries} />
            <BucketTable title="Browser-Kennungen (User-Agent)" buckets={data.userAgents} />
            <BucketTable title="Pfade" buckets={data.paths} />
            <BucketTable
              title="Erkennungsgründe"
              buckets={data.reasons}
              note="Signatur bzw. Sperrgrund, der zur Einstufung geführt hat."
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Letzte 100 Aufrufe</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Zeit</th>
                    <th className="py-2 pr-4 font-medium">Land</th>
                    <th className="py-2 pr-4 font-medium">Pfad</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Einstufung</th>
                    <th className="py-2 font-medium">User-Agent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.recent.map((r, i) => (
                    <tr key={`${r.created_at}-${i}`}>
                      <td className="py-2 pr-4 whitespace-nowrap tabular-nums">
                        {new Date(r.created_at).toLocaleString("de-DE")}
                      </td>
                      <td className="py-2 pr-4">{r.country ?? "–"}</td>
                      <td className="py-2 pr-4 break-all">{r.path}</td>
                      <td className="py-2 pr-4 tabular-nums">{r.status ?? "–"}</td>
                      <td className="py-2 pr-4">
                        {r.blocked ? (
                          <Badge variant="destructive">gesperrt</Badge>
                        ) : r.classification === "human" ? (
                          <Badge variant="outline">menschlich</Badge>
                        ) : r.classification === "known_good_bot" ? (
                          <Badge variant="secondary">Suchmaschine</Badge>
                        ) : (
                          <Badge variant="destructive">Bot</Badge>
                        )}
                      </td>
                      <td className="max-w-[24rem] truncate py-2 text-muted-foreground">
                        {r.user_agent ?? "–"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
